import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAthleteBySlug,
  Discipline,
  DISCIPLINE_LABELS,
  DISCIPLINE_COLORS,
} from '../data/athletes';
import { getAthleteProfile, AthleteProfile } from '../firebase/athleteService';
import ClassBadge from '../components/ClassBadge';

const DISCIPLINE_RESULT_PATHS: Record<Discipline, string> = {
  speed:   '/speed-schedule',
  boulder: '/boulder-results',
  lead:    '/lead-results',
};

const QA_FIELDS: Array<{ key: keyof AthleteProfile; label: string; emoji: string }> = [
  { key: 'animal',       label: 'If your climbing style were an animal…',           emoji: '🐾' },
  { key: 'hypeSong',     label: 'Ultimate hype song before getting on the wall',    emoji: '🎵' },
  { key: 'ritual',       label: 'Pre-competition rituals or superstitions',          emoji: '🔮' },
  { key: 'proudMoment',  label: 'Proudest or funniest climbing moment so far',       emoji: '🏆' },
  { key: 'climbWith',    label: 'Anyone in the world to climb with',                 emoji: '🌍' },
  { key: 'hiddenTalent', label: 'Hidden talent (unrelated to climbing)',             emoji: '✨' },
  { key: 'cheatMeal',    label: 'Ultimate post-competition cheat meal',              emoji: '🍕' },
];

function toDriveEmbed(url: string): string {
  // Convert drive.google.com/open?id=ID or /file/d/ID/ to thumbnail API URL
  const openMatch  = url.match(/[?&]id=([^&]+)/);
  const fileMatch  = url.match(/\/file\/d\/([^/]+)/);
  const id = openMatch?.[1] ?? fileMatch?.[1];
  if (id) return `https://lh3.googleusercontent.com/d/${id}`;
  return url; // already a direct URL
}

const AthleteProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate  = useNavigate();
  const athlete   = slug ? getAthleteBySlug(slug) : undefined;
  const [profile,  setProfile]  = useState<AthleteProfile | null | undefined>(undefined); // undefined = loading
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!slug) { setProfile(null); return; }
    setProfile(undefined);
    setImgError(false);
    getAthleteProfile(slug).then(setProfile).catch(() => setProfile(null));
  }, [slug]);

  // Go back if there's history, otherwise fall back to /athletes
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/athletes');
    }
  };

  if (!athlete) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-dark text-white gap-4">
        <p className="text-5xl mb-2">🧗</p>
        <p className="text-2xl font-bold">Athlete not found</p>
        <button onClick={goBack} className="text-blue-300 hover:text-white underline text-sm">
          ← Go back
        </button>
      </div>
    );
  }

  const pictureUrl = profile?.pictureUrl ? toDriveEmbed(profile.pictureUrl) : null;

  return (
    <div className="min-h-screen bg-dark text-white">

      {/* Hero */}
      <div className="bg-gradient-to-br from-dark via-secondary/60 to-dark px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={goBack}
            className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm mb-8 transition-colors"
          >
            ← Back
          </button>

          <div className="flex flex-col items-center gap-6 text-center">
            {/* Photo or flag */}
            {pictureUrl && !imgError ? (
              <img
                src={pictureUrl}
                alt={`${athlete.firstName} ${athlete.lastName}`}
                onError={() => setImgError(true)}
                className="w-64 h-80 sm:w-72 sm:h-96 rounded-2xl object-cover object-top border-2 border-white/20 shadow-2xl"
              />
            ) : (
              <span className="text-9xl leading-none">{athlete.flag}</span>
            )}
            <div>
              <h1 className="text-4xl font-extrabold tracking-tight">
                {athlete.firstName} {athlete.lastName}
              </h1>
              <p className="text-blue-300 mt-1.5 text-lg">
                {athlete.nationality} · {athlete.gender === 'male' ? 'Male' : 'Female'}
                {profile?.age ? ` · ${profile.age} y/o` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-5">

        {/* Classification */}
        <div className="bg-white/10 rounded-xl p-5">
          <h2 className="text-xs uppercase tracking-widest text-blue-300 mb-2">Classification</h2>
          <p className="text-3xl font-bold"><ClassBadge classId={athlete.classId} variant="large" /></p>
        </div>

        {/* Disciplines */}
        <div className="bg-white/10 rounded-xl p-5">
          <h2 className="text-xs uppercase tracking-widest text-blue-300 mb-3">Competing in</h2>
          <div className="flex flex-wrap gap-2">
            {athlete.disciplines.map(d => (
              <span key={d} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${DISCIPLINE_COLORS[d]}`}>
                {DISCIPLINE_LABELS[d]}
                {d === 'lead' && (
                  <span className="ml-1.5 opacity-60 text-xs font-normal">
                    ({athlete.isLead ? 'Lead' : 'Top rope'}{athlete.leadGroup ? ` · Group ${athlete.leadGroup}` : ''})
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Live results */}
        <div className="bg-white/10 rounded-xl p-5">
          <h2 className="text-xs uppercase tracking-widest text-blue-300 mb-3">Live results</h2>
          <div className="flex flex-col gap-2">
            {athlete.disciplines.map(d => (
              <button
                key={d}
                onClick={() => navigate(DISCIPLINE_RESULT_PATHS[d])}
                className="flex items-center justify-between bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 transition-colors text-left"
              >
                <span className="font-semibold text-sm">{DISCIPLINE_LABELS[d]} results</span>
                <span className="text-blue-300 text-sm">View →</span>
              </button>
            ))}
          </div>
        </div>

        {/* Q&A profile — loading / empty / filled */}
        {profile === undefined ? (
          <div className="bg-white/10 rounded-xl p-5 text-center text-blue-300 text-sm animate-pulse">
            Loading profile…
          </div>
        ) : profile === null || QA_FIELDS.every(f => !profile[f.key]) ? (
          <div className="bg-white/10 rounded-xl p-5 text-center text-white/40 text-sm">
            No profile info available yet.
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="text-xs uppercase tracking-widest text-blue-300 px-1">About the athlete</h2>
            {QA_FIELDS.filter(f => profile[f.key]).map(f => (
              <div key={f.key} className="bg-white/10 rounded-xl p-5">
                <p className="text-xs text-blue-300 mb-1.5">{f.emoji} {f.label}</p>
                <p className="text-white leading-relaxed">{profile[f.key]}</p>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default AthleteProfilePage;
